import { useDisclosure } from '@mantine/hooks';
import type { Cell } from '@tanstack/react-table';
import { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { type MouseEvent, type ReactNode, useCallback, useState } from 'react';
import type { Callback } from 'ts/base/Callback';
import { useUserPermissionInfo } from 'ts/base/hooks/PermissionInfoHook';
import { useNavigationHash } from 'ts/base/hooks/UseNavigationHash';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { ClipboardUtils } from 'ts/commons/ClipboardUtils';
import {
	type ExposedFindingCategoryFilterContext,
	InclusionType
} from 'ts/commons/filters/findings/FindingCategoryFilterContext';
import { useFindingFilterContextIfAvailable } from 'ts/commons/filters/findings/FindingsFilterContext';
import { openConfirmModal } from 'ts/commons/modal/ConfirmModal';
import { NavigationHash } from 'ts/commons/NavigationHash';
import { StringUtils } from 'ts/commons/StringUtils';
import { ContextMenuItem, ContextMenuSubMenuItem } from 'ts/components/ContextMenu';
import { ToastNotification } from 'ts/components/Toast';
import { isPending } from 'ts/perspectives/findings/details/FlaggedFindingUtils';
import { ExclusionRationaleModal } from 'ts/perspectives/findings/details/tabs/commons/ExclusionRationaleModal';
import { ResolvePendingExclusionRationaleModal } from 'ts/perspectives/findings/details/tabs/commons/ResolvePendingExclusionRationaleModal';
import {
	type FlaggingPermission,
	useBlacklistPermissions
} from 'ts/perspectives/findings/details/tabs/commons/UseBlacklistPermissions';
import { FindingsUtils } from 'ts/perspectives/findings/FindingsUtils';
import { type CellContextMenuHandler, useContextMenu } from 'ts/perspectives/findings/list/ContextMenu';
import { invalidateFindingsList } from 'ts/perspectives/findings/list/FindingsTable.services';
import { openPathBreadcrumbModal } from 'ts/perspectives/findings/list/PathBreadcrumbDialog';
import { openTaskSelectionModal } from 'ts/perspectives/quality_control/tasks/dialog/TaskSelectionDialog';
import { EFindingBlacklistType, type EFindingBlacklistTypeEntry } from 'typedefs/EFindingBlacklistType';
import { EProjectPermission } from 'typedefs/EProjectPermission';
import type { ExtendedTrackedFinding } from 'typedefs/ExtendedTrackedFinding';
import type { TrackedFinding } from 'typedefs/TrackedFinding';

/** An enum specifying the different possible types of a context menu filter action. */
enum FindingListFilterType {
	CATEGORY,
	GROUP,
	TYPE
}

/** Event callback that handles showing the context menu after a corresponding mouse event. */
export type FindingContextMenuHandler = CellContextMenuHandler<ExtendedTrackedFinding>;

type RationaleModalType = 'flagging' | 'resolve';
type RationaleModalContext = { type: RationaleModalType } & (
	| {
			type: 'flagging';
			flaggingType: EFindingBlacklistType;
	  }
	| {
			type: 'resolve';
			approve: boolean;
	  }
);

/**
 * Helper hook for the findings context menu that provides a onContextMenuHandler that can be applied on the table rows
 * as well as a factory function that creates the actual context menu. To support the quick actions for adding filter
 * entries, the context menu must be wrapped with a FindingsFilterContext.
 *
 * @param commit The commit at which the findings will be flagged when the action is selected.
 */
export function useFindingsContextMenu(commit: UnresolvedCommitDescriptor | null): {
	contextMenu: ReactNode;
	modal: ReactNode;
	onContextMenu: CellContextMenuHandler<ExtendedTrackedFinding>;
} {
	const { contextMenu, showContextMenu } = useContextMenu();

	const [rationaleModalOpened, { open, close }] = useDisclosure(false);
	const [modalContext, setModalContext] = useState<RationaleModalContext | undefined>();
	const [selectedFindings, setSelectedFindings] = useState<ExtendedTrackedFinding[]>([]);
	const containsSpecItems = selectedFindings.some(f => FindingsUtils.isSpecItemFindingLocation(f.location));

	if (containsSpecItems) {
		commit = getCommitFromSpecItems(selectedFindings, commit);
	}

	const openRationaleModal = useCallback(
		(context: RationaleModalContext, findings: ExtendedTrackedFinding[]) => {
			setSelectedFindings(findings);
			setModalContext(context);
			open();
		},
		[open]
	);

	return {
		contextMenu,
		modal:
			rationaleModalOpened && modalContext !== undefined ? (
				modalContext.type === 'flagging' ? (
					<ExclusionRationaleModal
						updateQueriesOptimistically
						onClose={close}
						onSuccess={reloadCurrentTab}
						exclusionType={modalContext.flaggingType}
						findingsToExclude={selectedFindings}
						commit={commit}
					/>
				) : (
					<ResolvePendingExclusionRationaleModal
						findingIds={selectedFindings.map(finding => finding.id)}
						commit={commit}
						approve={modalContext.approve}
						onClose={close}
						onSuccess={reloadCurrentTab}
					/>
				)
			) : null,
		onContextMenu: useCallback(
			(
				e: MouseEvent,
				newSelectedFindings: ExtendedTrackedFinding[],
				cell: Cell<ExtendedTrackedFinding, unknown>
			) => {
				showContextMenu(
					e,
					<FindingContextMenu
						columnId={cell.column.id}
						focusedFinding={cell.row.original}
						selectedFindings={newSelectedFindings}
						openRationaleModal={context => openRationaleModal(context, newSelectedFindings)}
					/>
				);
			},
			[openRationaleModal, showContextMenu]
		)
	};
}

/** Props for FindingContextMenu. */
type FindingContextMenuProps = {
	/** The column ID in which the right click was performed. */
	columnId: string;
	/** The finding that the user performed the actual right click on even when more findings are selected. */
	focusedFinding: ExtendedTrackedFinding;
	/** The selected findings. */
	selectedFindings: ExtendedTrackedFinding[];
	/** Callback to open the rationale dialog. */
	openRationaleModal: Callback<RationaleModalContext>;
};

const CONTEXT_MENU_LOOKUP: Record<EFindingBlacklistTypeEntry, { key: string; humanReadable: string }> = {
	TOLERATION: {
		key: 'tolerated',
		humanReadable: 'Toleration'
	},
	FALSE_POSITIVE: {
		key: 'false-positive',
		humanReadable: 'False Positive'
	}
};

function getFlaggingTypeSpecificContextMenuItems(
	findings: ExtendedTrackedFinding[],
	type: EFindingBlacklistType,
	permissions: FlaggingPermission[],
	unflagAction: Callback<void>,
	resolveSuggestionAction: Callback<RationaleModalContext>
): ReactNode[] {
	if (findings.length === 0) {
		return [];
	}
	const { key, humanReadable } = CONTEXT_MENU_LOOKUP[type.name];
	const onlyPending = findings.every(finding => isPending(finding.blacklistInfo!.approvalState));
	const items: ReactNode[] = [
		<ContextMenuItem
			key={'unflag-' + key}
			data-testid={'unflag-' + key}
			// When all are pending, we use a different wording
			text={`Remove from ${onlyPending ? 'Pending ' : ''}${humanReadable}s`}
			icon="undo"
			onClick={() => unflagAction()}
		/>
	];
	if (permissions.includes('Approve')) {
		const resolveItems = getResolvePendingExclusionContextMenuItems(type, findings, approve =>
			resolveSuggestionAction({
				type: 'resolve',
				approve
			})
		);
		items.push(...resolveItems);
	}
	return items;
}

function getResolvePendingExclusionContextMenuItems(
	type: EFindingBlacklistType,
	findings: ExtendedTrackedFinding[],
	resolveSuggestionAction: Callback<boolean>
): ReactNode[] {
	const { key, humanReadable } = CONTEXT_MENU_LOOKUP[type.name];
	return [
		<ContextMenuItem
			key={'approve-' + key}
			data-testid={'approve-' + key}
			text={'Approve as ' + humanReadable}
			icon="check square outline"
			onClick={() => resolveSuggestionAction(true)}
		/>,
		<ContextMenuItem
			key={'reject-' + key}
			data-testid={'reject-' + key}
			text={'Reject ' + StringUtils.pluralize(findings, humanReadable)}
			icon="close"
			onClick={() => resolveSuggestionAction(false)}
		/>
	];
}

function useFlaggingContextMenuItems(
	selectedFindings: ExtendedTrackedFinding[],
	openRationaleModal: Callback<RationaleModalContext>
): ReactNode[] {
	const projectId = useProjectId();
	const {
		canFlagAsFalsePositive,
		canFlagAsToleration,
		detailedPermissions: { TOLERATION: tolerationPermissions, FALSE_POSITIVE: falsePositivePermissions }
	} = useBlacklistPermissions(
		selectedFindings,
		selectedFindings.map(finding => finding.blacklistInfo ?? null)
	);
	if (tolerationPermissions.length === 0 && falsePositivePermissions.length === 0) {
		return [];
	}
	const items: ReactNode[] = [];
	const falsePositives = selectedFindings.filter(
		finding => finding.blacklistInfo?.type === EFindingBlacklistType.FALSE_POSITIVE.name
	);
	const tolerated = selectedFindings.filter(
		finding => finding.blacklistInfo?.type === EFindingBlacklistType.TOLERATION.name
	);
	const containsUnflaggedFindings = selectedFindings.some(finding => finding.blacklistInfo?.type == null);
	if (canFlagAsFalsePositive && (containsUnflaggedFindings || tolerated.length > 0)) {
		// flagging findings as false positive
		items.push(
			<ContextMenuItem
				key="false-positive"
				data-testid="flag-as-false-positive"
				text="Mark as False Positive"
				icon="false positive"
				onClick={() =>
					openRationaleModal({
						type: 'flagging',
						flaggingType: EFindingBlacklistType.FALSE_POSITIVE
					})
				}
			/>
		);
	}
	if (canFlagAsToleration && (containsUnflaggedFindings || falsePositives.length > 0)) {
		// flagging findings as tolerated
		items.push(
			<ContextMenuItem
				key="tolerated"
				data-testid="flag-as-tolerated"
				text="Tolerate"
				icon="toleration"
				onClick={() =>
					openRationaleModal({
						type: 'flagging',
						flaggingType: EFindingBlacklistType.TOLERATION
					})
				}
			/>
		);
	}
	items.push(
		...getFlaggingTypeSpecificContextMenuItems(
			falsePositives,
			EFindingBlacklistType.FALSE_POSITIVE,
			falsePositivePermissions,
			() => handleConfirmAndUnmarkFindings(EFindingBlacklistType.FALSE_POSITIVE, projectId, selectedFindings),
			openRationaleModal
		)
	);
	items.push(
		...getFlaggingTypeSpecificContextMenuItems(
			tolerated,
			EFindingBlacklistType.TOLERATION,
			falsePositivePermissions,
			() => handleConfirmAndUnmarkFindings(EFindingBlacklistType.TOLERATION, projectId, selectedFindings),
			openRationaleModal
		)
	);
	return items;
}

/** Provides the context menu for the findings tables. */
function FindingContextMenu({
	columnId,
	focusedFinding,
	selectedFindings,
	openRationaleModal
}: FindingContextMenuProps) {
	const hash = useNavigationHash();
	const permissionInfo = useUserPermissionInfo();
	const projectId = hash.getProject();
	const findingFilterContext = useFindingFilterContextIfAvailable();
	const items: ReactNode[] = [];
	const selectedFindingIds = selectedFindings.map(finding => finding.id);
	if (permissionInfo.hasProjectPermission(projectId, EProjectPermission.EDIT_TASKS)) {
		// adding selected findings to task
		items.push(
			<ContextMenuItem
				key="add-to-task"
				data-testid="add-to-task"
				text="Add to Task"
				icon="plus"
				onClick={() => launchTaskSelectionDialog(projectId, selectedFindings)}
			/>
		);
	}

	const flaggingItems = useFlaggingContextMenuItems(selectedFindings, openRationaleModal);
	items.push(...flaggingItems);

	// copying the selected findings ids.
	items.push(
		<ContextMenuItem
			key="copy"
			data-testid="copy-finding-id"
			text="Copy finding ID"
			icon="copy"
			onClick={() => void ClipboardUtils.copyTextToClipboard(selectedFindingIds.join())}
		/>
	);

	if (columnId === 'uniformPath' && findingFilterContext) {
		items.push(
			<ContextMenuItem
				key="new-include"
				data-testid="new-include-path-filter"
				text="New include path filter"
				icon="search plus"
				onClick={() => openPathBreadcrumbModal(focusedFinding, true, findingFilterContext)}
			/>
		);
		items.push(
			<ContextMenuItem
				key="new-exclude"
				data-testid="new-exclude-path-filter"
				text="New exclude path filter"
				icon="search minus"
				onClick={() => openPathBreadcrumbModal(focusedFinding, false, findingFilterContext)}
			/>
		);
	}

	if (columnId === 'group' && findingFilterContext) {
		items.push(
			<IncludeExcludeGroupItemsSubMenu
				key="include-group-filter"
				finding={focusedFinding}
				categoryFilterContext={findingFilterContext}
				include={InclusionType.INCLUDE_ONLY}
				text="New include filter..."
				dataTestId="new-include-group-filter"
			/>
		);
		items.push(
			<IncludeExcludeGroupItemsSubMenu
				key="exclude-group-filter"
				finding={focusedFinding}
				categoryFilterContext={findingFilterContext}
				include={InclusionType.ADD_EXCLUDE}
				text="New exclude filter..."
				dataTestId="new-exclude-group-filter"
			/>
		);
	}

	return <>{items}</>;
}

/**
 * Handles clicks to the include/exclude filter context menu entries.
 *
 * @param filterType The type of the filter, depending on which context menu entry was clicked.
 * @param include Indicates whether the include or exclude menu item was clicked.
 */
function handleFilterMenuItemClick(
	categoryFilterContext: ExposedFindingCategoryFilterContext,
	selectedFinding: ExtendedTrackedFinding,
	filterType: FindingListFilterType,
	include: InclusionType
): void {
	switch (filterType) {
		case FindingListFilterType.CATEGORY:
			categoryFilterContext.toggleFindingCategory(
				categoryFilterContext.categories.find(
					category => category.categoryName === selectedFinding.categoryName
				)!,
				include
			);
			break;
		case FindingListFilterType.GROUP:
			categoryFilterContext.toggleFindingGroup(
				categoryFilterContext.groups.find(category => category.groupName === selectedFinding.groupName)!,
				include
			);
			break;
		case FindingListFilterType.TYPE:
			categoryFilterContext.toggleFindingType(
				categoryFilterContext.findingTypes.find(
					findingType => findingType.typeIdName === selectedFinding.typeId
				)!,
				include
			);
			break;
		default:
			return;
	}
}

type IncludeExcludeGroupItemsSubMenuProps = {
	finding: ExtendedTrackedFinding;
	categoryFilterContext: ExposedFindingCategoryFilterContext;
	include: InclusionType;
	text: string;
	dataTestId: string;
};

/** Provides the items' menu for the include/exclude group option. */
function IncludeExcludeGroupItemsSubMenu({
	finding,
	categoryFilterContext,
	include,
	text,
	dataTestId
}: IncludeExcludeGroupItemsSubMenuProps) {
	return (
		<ContextMenuSubMenuItem text={text} data-testid={dataTestId}>
			<ContextMenuItem
				text="Type"
				data-testid="Type"
				onClick={() =>
					handleFilterMenuItemClick(categoryFilterContext, finding, FindingListFilterType.TYPE, include)
				}
			/>
			<ContextMenuItem
				text="Analysis group"
				data-testid="Group"
				onClick={() =>
					handleFilterMenuItemClick(categoryFilterContext, finding, FindingListFilterType.GROUP, include)
				}
			/>
			<ContextMenuItem
				text="Quality indicator"
				data-testid="Category"
				onClick={() =>
					handleFilterMenuItemClick(categoryFilterContext, finding, FindingListFilterType.CATEGORY, include)
				}
			/>
		</ContextMenuSubMenuItem>
	);
}

/** Reloads the current view of ordinary, tolerated or false positive findings. */
function reloadCurrentTab(): void {
	// When we directly reload the update has not happened yet on the backend
	// We wait for a second and refresh then in the hope that the blacklist operation was already processed
	setTimeout(() => NavigationHash.getCurrent().reload(), 1000);
}

function launchTaskSelectionDialog(projectId: string, findings: ExtendedTrackedFinding[]): void {
	const commit = NavigationHash.getCurrentCommit();
	const taskDetails = FindingsUtils.getTaskDataContainerForFindings(
		findings,
		commit,
		findings.filter(finding => finding.blacklistInfo != null).map(finding => finding.id)
	);
	openTaskSelectionModal(projectId, taskDetails, invalidateFindingsList);
}

function handleConfirmAndUnmarkFindings(
	blacklistType: EFindingBlacklistType,
	projectId: string,
	selectedFindings: TrackedFinding[]
) {
	confirmAndUnmarkFindings(
		selectedFindings.map(finding => finding.id),
		blacklistType,
		() =>
			FindingsUtils.unblacklistFindings(
				projectId,
				selectedFindings,
				NavigationHash.getCurrentCommit(),
				blacklistType
			).then(() => reloadCurrentTab())
	);
}

/** Displays a dialog box to confirm from user if to go ahead with un-marking findings as tolerated or false positives. */
function confirmAndUnmarkFindings(
	findingIds: string[],
	blacklistType: EFindingBlacklistType,
	unmarkFindingsCallback: () => void | Promise<void>
): void {
	let blacklistText = 'false positives';
	if (blacklistType === EFindingBlacklistType.TOLERATION) {
		blacklistText = 'tolerated findings';
	}
	const selectedFindingsCount = findingIds.length;
	let question = 'Do you really want to remove ';
	if (selectedFindingsCount > 1) {
		question += 'these ' + selectedFindingsCount + ' findings from ' + blacklistText + '?';
	} else {
		question += 'this finding from ' + blacklistText + '?';
	}
	openConfirmModal({
		title: 'Confirmation needed',
		content: question,
		confirmText: 'Remove',
		onConfirm: unmarkFindingsCallback
	});
}

function getCommitFromSpecItems(selectedFindings: ExtendedTrackedFinding[], commit: UnresolvedCommitDescriptor | null) {
	const branches = selectedFindings.map(f => f.birth.branchName);
	if (branches.length > 1) {
		ToastNotification.error('To mark multiple findings, they must belong to the same spec item connector.');
	}
	// Spec item findings are located on a separate virtual branch, therefore we cannot use the selected branch.
	return new UnresolvedCommitDescriptor(commit?.timestamp, branches[0]);
}
